
import { SQLModule } from '../types';

// Additional modules for SQL Advanced course
export const SQL_ADVANCED_EXPANDED_MODULES: SQLModule[] = [
  {
    id: 'sql-advanced-3',
    title: 'Views, Stored Procedures and Functions',
    description: 'Learn to create and manage views, stored procedures, and user-defined functions for modular database design.',
    content: `# Views, Stored Procedures and Functions

## Database Views

### Simple Views

\`\`\`sql
-- Create a simple view for customer order summary
CREATE VIEW customer_order_summary AS
SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    c.email,
    COUNT(o.order_id) as total_orders,
    COALESCE(SUM(o.total_amount), 0) as total_spent,
    COALESCE(AVG(o.total_amount), 0) as avg_order_value,
    MAX(o.order_date) as last_order_date
FROM customers c
LEFT JOIN orders o ON c.customer_id = o.customer_id 
    AND o.order_status = 'completed'
GROUP BY c.customer_id, c.first_name, c.last_name, c.email;

-- Use the view
SELECT * FROM customer_order_summary 
WHERE total_spent > 1000
ORDER BY total_spent DESC;

-- Create a product performance view
CREATE VIEW product_performance AS
SELECT 
    p.product_id,
    p.product_name,
    c.category_name,
    p.price,
    p.stock_quantity,
    COALESCE(sales_data.units_sold, 0) as units_sold,
    COALESCE(sales_data.revenue, 0) as total_revenue,
    COALESCE(review_data.avg_rating, 0) as avg_rating,
    COALESCE(review_data.review_count, 0) as review_count
FROM products p
JOIN categories c ON p.category_id = c.category_id
LEFT JOIN (
    SELECT 
        oi.product_id,
        SUM(oi.quantity) as units_sold,
        SUM(oi.total_price) as revenue
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_status = 'completed'
    GROUP BY oi.product_id
) sales_data ON p.product_id = sales_data.product_id
LEFT JOIN (
    SELECT 
        product_id,
        AVG(rating) as avg_rating,
        COUNT(*) as review_count
    FROM reviews
    GROUP BY product_id
) review_data ON p.product_id = review_data.product_id
WHERE p.is_active = TRUE;
\`\`\`

### Complex Views with Business Logic

\`\`\`sql
-- Advanced customer segmentation view
CREATE VIEW customer_segments AS
SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    c.email,
    c.created_at as customer_since,
    COALESCE(order_stats.total_orders, 0) as total_orders,
    COALESCE(order_stats.total_spent, 0) as total_spent,
    COALESCE(order_stats.avg_order_value, 0) as avg_order_value,
    COALESCE(order_stats.last_order_date, c.created_at) as last_order_date,
    DATEDIFF(NOW(), COALESCE(order_stats.last_order_date, c.created_at)) as days_since_last_order,
    
    -- Customer lifetime value calculation
    CASE 
        WHEN order_stats.total_orders > 0 THEN
            order_stats.total_spent / GREATEST(DATEDIFF(NOW(), c.created_at) / 365.0, 0.1)
        ELSE 0
    END as estimated_clv,
    
    -- Customer segment assignment
    CASE 
        WHEN order_stats.total_spent >= 10000 AND order_stats.total_orders >= 20 THEN 'VIP Frequent'
        WHEN order_stats.total_spent >= 10000 THEN 'VIP'
        WHEN order_stats.total_spent >= 5000 THEN 'High Value'
        WHEN order_stats.total_spent >= 1000 THEN 'Regular'
        WHEN order_stats.total_orders > 0 THEN 'Low Value'
        ELSE 'Prospect'
    END as customer_segment,
    
    -- Activity status
    CASE 
        WHEN DATEDIFF(NOW(), COALESCE(order_stats.last_order_date, c.created_at)) <= 30 THEN 'Active'
        WHEN DATEDIFF(NOW(), COALESCE(order_stats.last_order_date, c.created_at)) <= 90 THEN 'At Risk'
        WHEN DATEDIFF(NOW(), COALESCE(order_stats.last_order_date, c.created_at)) <= 365 THEN 'Dormant'
        ELSE 'Inactive'
    END as activity_status
    
FROM customers c
LEFT JOIN (
    SELECT 
        customer_id,
        COUNT(*) as total_orders,
        SUM(total_amount) as total_spent,
        AVG(total_amount) as avg_order_value,
        MAX(order_date) as last_order_date
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY customer_id
) order_stats ON c.customer_id = order_stats.customer_id
WHERE c.is_active = TRUE;

-- Inventory management view
CREATE VIEW inventory_alerts AS
SELECT 
    p.product_id,
    p.product_name,
    c.category_name,
    p.stock_quantity,
    p.price,
    COALESCE(sales_velocity.avg_daily_sales, 0) as avg_daily_sales,
    
    -- Days of inventory remaining
    CASE 
        WHEN COALESCE(sales_velocity.avg_daily_sales, 0) > 0 
        THEN p.stock_quantity / sales_velocity.avg_daily_sales
        ELSE 999
    END as days_of_inventory,
    
    -- Reorder recommendations
    CASE 
        WHEN p.stock_quantity = 0 THEN 'OUT_OF_STOCK'
        WHEN sales_velocity.avg_daily_sales > 0 AND 
             p.stock_quantity / sales_velocity.avg_daily_sales < 7 THEN 'URGENT_REORDER'
        WHEN sales_velocity.avg_daily_sales > 0 AND 
             p.stock_quantity / sales_velocity.avg_daily_sales < 14 THEN 'LOW_STOCK'
        WHEN sales_velocity.avg_daily_sales > 0 AND 
             p.stock_quantity / sales_velocity.avg_daily_sales > 90 THEN 'OVERSTOCK'
        ELSE 'NORMAL'
    END as stock_status,
    
    -- Recommended order quantity
    CASE 
        WHEN sales_velocity.avg_daily_sales > 0 
        THEN GREATEST(0, (sales_velocity.avg_daily_sales * 30) - p.stock_quantity)
        ELSE 0
    END as suggested_reorder_qty
    
FROM products p
JOIN categories c ON p.category_id = c.category_id
LEFT JOIN (
    SELECT 
        oi.product_id,
        SUM(oi.quantity) / 30.0 as avg_daily_sales
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_status = 'completed'
        AND o.order_date >= DATE_SUB(NOW(), INTERVAL 30 DAY)
    GROUP BY oi.product_id
) sales_velocity ON p.product_id = sales_velocity.product_id
WHERE p.is_active = TRUE;
\`\`\`

### Updatable Views

\`\`\`sql
-- Create an updatable view for customer contact info
CREATE VIEW customer_contacts AS
SELECT 
    customer_id,
    first_name,
    last_name,
    email,
    phone,
    address,
    city,
    state,
    zip_code
FROM customers
WHERE is_active = TRUE;

-- Update through the view
UPDATE customer_contacts 
SET phone = '555-0123', email = 'new.email@example.com'
WHERE customer_id = 1;

-- Create view with check option
CREATE VIEW active_products AS
SELECT 
    product_id,
    product_name,
    category_id,
    price,
    stock_quantity,
    is_active
FROM products
WHERE is_active = TRUE
WITH CHECK OPTION;

-- This will work
INSERT INTO active_products (product_name, category_id, price, stock_quantity, is_active)
VALUES ('New Product', 1, 29.99, 100, TRUE);

-- This will fail due to CHECK OPTION
-- INSERT INTO active_products (product_name, category_id, price, stock_quantity, is_active)
-- VALUES ('Inactive Product', 1, 29.99, 100, FALSE);
\`\`\`

## Stored Procedures

### Basic Stored Procedures

\`\`\`sql
-- Simple stored procedure for order processing
DELIMITER //

CREATE PROCEDURE ProcessOrder(
    IN p_customer_id INT,
    IN p_product_id INT,
    IN p_quantity INT,
    OUT p_order_id INT,
    OUT p_status_message VARCHAR(255)
)
BEGIN
    DECLARE v_product_price DECIMAL(10,2);
    DECLARE v_stock_quantity INT;
    DECLARE v_total_amount DECIMAL(12,2);
    DECLARE EXIT HANDLER FOR SQLEXCEPTION
    BEGIN
        ROLLBACK;
        SET p_status_message = 'Error occurred during order processing';
        SET p_order_id = -1;
    END;
    
    START TRANSACTION;
    
    -- Check product availability
    SELECT price, stock_quantity 
    INTO v_product_price, v_stock_quantity
    FROM products 
    WHERE product_id = p_product_id AND is_active = TRUE;
    
    IF v_stock_quantity < p_quantity THEN
        SET p_status_message = 'Insufficient stock';
        SET p_order_id = -1;
        ROLLBACK;
    ELSE
        -- Calculate total
        SET v_total_amount = v_product_price * p_quantity;
        
        -- Create order
        INSERT INTO orders (customer_id, order_date, order_status, total_amount)
        VALUES (p_customer_id, NOW(), 'processing', v_total_amount);
        
        SET p_order_id = LAST_INSERT_ID();
        
        -- Add order item
        INSERT INTO order_items (order_id, product_id, quantity, unit_price, total_price)
        VALUES (p_order_id, p_product_id, p_quantity, v_product_price, v_total_amount);
        
        -- Update inventory
        UPDATE products 
        SET stock_quantity = stock_quantity - p_quantity
        WHERE product_id = p_product_id;
        
        SET p_status_message = 'Order processed successfully';
        COMMIT;
    END IF;
END //

DELIMITER ;

-- Call the stored procedure
CALL ProcessOrder(123, 456, 2, @order_id, @status);
SELECT @order_id as order_id, @status as status;
\`\`\`

### Complex Stored Procedures with Business Logic

\`\`\`sql
DELIMITER //

CREATE PROCEDURE CalculateCustomerLoyaltyRewards(
    IN p_customer_id INT,
    IN p_calculation_date DATE,
    OUT p_points_earned INT,
    OUT p_tier_status VARCHAR(50),
    OUT p_rewards_summary JSON
)
BEGIN
    DECLARE v_total_spent DECIMAL(12,2) DEFAULT 0;
    DECLARE v_order_count INT DEFAULT 0;
    DECLARE v_avg_order_value DECIMAL(10,2) DEFAULT 0;
    DECLARE v_months_active INT DEFAULT 0;
    DECLARE v_points_multiplier DECIMAL(3,2) DEFAULT 1.0;
    DECLARE v_bonus_points INT DEFAULT 0;
    
    -- Calculate customer metrics
    SELECT 
        COALESCE(SUM(total_amount), 0),
        COUNT(*),
        COALESCE(AVG(total_amount), 0),
        TIMESTAMPDIFF(MONTH, MIN(order_date), p_calculation_date)
    INTO v_total_spent, v_order_count, v_avg_order_value, v_months_active
    FROM orders
    WHERE customer_id = p_customer_id 
        AND order_status = 'completed'
        AND order_date <= p_calculation_date;
    
    -- Determine tier and multiplier
    CASE
        WHEN v_total_spent >= 10000 THEN
            BEGIN
                SET p_tier_status = 'Platinum';
                SET v_points_multiplier = 2.0;
                SET v_bonus_points = 500;
            END;
        WHEN v_total_spent >= 5000 THEN
            BEGIN
                SET p_tier_status = 'Gold';
                SET v_points_multiplier = 1.5;
                SET v_bonus_points = 250;
            END;
        WHEN v_total_spent >= 1000 THEN
            BEGIN
                SET p_tier_status = 'Silver';
                SET v_points_multiplier = 1.2;
                SET v_bonus_points = 100;
            END;
        ELSE
            BEGIN
                SET p_tier_status = 'Bronze';
                SET v_points_multiplier = 1.0;
                SET v_bonus_points = 0;
            END;
    END CASE;
    
    -- Calculate base points (1 point per dollar spent)
    SET p_points_earned = FLOOR(v_total_spent * v_points_multiplier) + v_bonus_points;
    
    -- Add frequency bonus (extra points for frequent orders)
    IF v_order_count >= 20 THEN
        SET p_points_earned = p_points_earned + 200;
    ELSEIF v_order_count >= 10 THEN
        SET p_points_earned = p_points_earned + 100;
    END IF;
    
    -- Add longevity bonus
    IF v_months_active >= 12 THEN
        SET p_points_earned = p_points_earned + (v_months_active * 10);
    END IF;
    
    -- Create rewards summary
    SET p_rewards_summary = JSON_OBJECT(
        'total_spent', v_total_spent,
        'order_count', v_order_count,
        'avg_order_value', v_avg_order_value,
        'months_active', v_months_active,
        'points_multiplier', v_points_multiplier,
        'base_points', FLOOR(v_total_spent * v_points_multiplier),
        'tier_bonus', v_bonus_points,
        'frequency_bonus', CASE 
            WHEN v_order_count >= 20 THEN 200
            WHEN v_order_count >= 10 THEN 100
            ELSE 0
        END,
        'longevity_bonus', CASE 
            WHEN v_months_active >= 12 THEN v_months_active * 10
            ELSE 0
        END,
        'total_points', p_points_earned
    );
    
    -- Update customer loyalty record
    INSERT INTO customer_loyalty (
        customer_id, 
        calculation_date, 
        tier_status, 
        points_earned, 
        rewards_summary
    )
    VALUES (
        p_customer_id, 
        p_calculation_date, 
        p_tier_status, 
        p_points_earned, 
        p_rewards_summary
    )
    ON DUPLICATE KEY UPDATE
        tier_status = VALUES(tier_status),
        points_earned = VALUES(points_earned),
        rewards_summary = VALUES(rewards_summary),
        updated_at = NOW();
        
END //

DELIMITER ;
\`\`\`

### Stored Procedures with Cursors

\`\`\`sql
DELIMITER //

CREATE PROCEDURE ProcessMonthlyInventoryReport(
    IN p_year INT,
    IN p_month INT
)
BEGIN
    DECLARE done INT DEFAULT FALSE;
    DECLARE v_product_id INT;
    DECLARE v_product_name VARCHAR(255);
    DECLARE v_category_name VARCHAR(100);
    DECLARE v_stock_quantity INT;
    DECLARE v_units_sold INT;
    DECLARE v_revenue DECIMAL(12,2);
    DECLARE v_avg_rating DECIMAL(3,2);
    
    -- Cursor for all active products
    DECLARE product_cursor CURSOR FOR
        SELECT 
            p.product_id,
            p.product_name,
            c.category_name,
            p.stock_quantity
        FROM products p
        JOIN categories c ON p.category_id = c.category_id
        WHERE p.is_active = TRUE;
    
    DECLARE CONTINUE HANDLER FOR NOT FOUND SET done = TRUE;
    
    -- Create temporary table for report
    CREATE TEMPORARY TABLE monthly_report (
        product_id INT,
        product_name VARCHAR(255),
        category_name VARCHAR(100),
        stock_quantity INT,
        units_sold INT,
        revenue DECIMAL(12,2),
        avg_rating DECIMAL(3,2),
        stock_status VARCHAR(50),
        performance_category VARCHAR(50)
    );
    
    OPEN product_cursor;
    
    read_loop: LOOP
        FETCH product_cursor INTO v_product_id, v_product_name, v_category_name, v_stock_quantity;
        
        IF done THEN
            LEAVE read_loop;
        END IF;
        
        -- Get sales data for the month
        SELECT 
            COALESCE(SUM(oi.quantity), 0),
            COALESCE(SUM(oi.total_price), 0)
        INTO v_units_sold, v_revenue
        FROM order_items oi
        JOIN orders o ON oi.order_id = o.order_id
        WHERE oi.product_id = v_product_id
            AND YEAR(o.order_date) = p_year
            AND MONTH(o.order_date) = p_month
            AND o.order_status = 'completed';
        
        -- Get average rating
        SELECT COALESCE(AVG(rating), 0)
        INTO v_avg_rating
        FROM reviews
        WHERE product_id = v_product_id;
        
        -- Insert into report table
        INSERT INTO monthly_report VALUES (
            v_product_id,
            v_product_name,
            v_category_name,
            v_stock_quantity,
            v_units_sold,
            v_revenue,
            v_avg_rating,
            CASE 
                WHEN v_stock_quantity = 0 THEN 'Out of Stock'
                WHEN v_stock_quantity < 10 THEN 'Low Stock'
                WHEN v_stock_quantity > 100 THEN 'Overstock'
                ELSE 'Normal'
            END,
            CASE 
                WHEN v_revenue > 1000 AND v_avg_rating >= 4.0 THEN 'Star Performer'
                WHEN v_revenue > 500 THEN 'Good Performer'
                WHEN v_revenue > 100 THEN 'Average Performer'
                ELSE 'Poor Performer'
            END
        );
        
    END LOOP;
    
    CLOSE product_cursor;
    
    -- Return the report
    SELECT * FROM monthly_report
    ORDER BY revenue DESC, units_sold DESC;
    
    -- Clean up
    DROP TEMPORARY TABLE monthly_report;
    
END //

DELIMITER ;
\`\`\`

## User-Defined Functions

### Scalar Functions

\`\`\`sql
DELIMITER //

-- Function to calculate shipping cost
CREATE FUNCTION CalculateShippingCost(
    p_order_total DECIMAL(10,2),
    p_shipping_method VARCHAR(50),
    p_customer_tier VARCHAR(50)
) RETURNS DECIMAL(8,2)
READS SQL DATA
DETERMINISTIC
BEGIN
    DECLARE v_shipping_cost DECIMAL(8,2) DEFAULT 0;
    DECLARE v_tier_discount DECIMAL(3,2) DEFAULT 1.0;
    
    -- Apply tier discount
    CASE p_customer_tier
        WHEN 'Platinum' THEN SET v_tier_discount = 0.0;  -- Free shipping
        WHEN 'Gold' THEN SET v_tier_discount = 0.5;      -- 50% off
        WHEN 'Silver' THEN SET v_tier_discount = 0.8;    -- 20% off
        ELSE SET v_tier_discount = 1.0;                  -- No discount
    END CASE;
    
    -- Calculate base shipping cost
    CASE p_shipping_method
        WHEN 'standard' THEN 
            SET v_shipping_cost = CASE 
                WHEN p_order_total >= 100 THEN 0
                WHEN p_order_total >= 50 THEN 5.99
                ELSE 9.99
            END;
        WHEN 'express' THEN 
            SET v_shipping_cost = 15.99;
        WHEN 'overnight' THEN 
            SET v_shipping_cost = 29.99;
        ELSE 
            SET v_shipping_cost = 9.99;
    END CASE;
    
    RETURN v_shipping_cost * v_tier_discount;
END //

-- Function to format customer name
CREATE FUNCTION FormatCustomerName(
    p_first_name VARCHAR(50),
    p_last_name VARCHAR(50),
    p_format_type VARCHAR(20)
) RETURNS VARCHAR(105)
READS SQL DATA
DETERMINISTIC
BEGIN
    CASE p_format_type
        WHEN 'full' THEN 
            RETURN CONCAT(TRIM(p_first_name), ' ', TRIM(p_last_name));
        WHEN 'last_first' THEN 
            RETURN CONCAT(TRIM(p_last_name), ', ', TRIM(p_first_name));
        WHEN 'initials' THEN 
            RETURN CONCAT(LEFT(TRIM(p_first_name), 1), '.', LEFT(TRIM(p_last_name), 1), '.');
        WHEN 'formal' THEN 
            RETURN CONCAT('Mr./Ms. ', TRIM(p_last_name));
        ELSE 
            RETURN CONCAT(TRIM(p_first_name), ' ', TRIM(p_last_name));
    END CASE;
END //

DELIMITER ;

-- Use the functions
SELECT 
    order_id,
    customer_id,
    total_amount,
    shipping_method,
    CalculateShippingCost(total_amount, shipping_method, 'Gold') as shipping_cost
FROM orders;

SELECT 
    customer_id,
    FormatCustomerName(first_name, last_name, 'formal') as formal_name,
    FormatCustomerName(first_name, last_name, 'initials') as initials
FROM customers;
\`\`\`

### Table-Valued Functions (Simulated with Procedures)

\`\`\`sql
DELIMITER //

-- Procedure that returns a result set (simulates table function)
CREATE PROCEDURE GetTopProductsByCategory(
    IN p_category_id INT,
    IN p_limit INT,
    IN p_date_from DATE,
    IN p_date_to DATE
)
BEGIN
    SELECT 
        p.product_id,
        p.product_name,
        p.price,
        COALESCE(SUM(oi.quantity), 0) as units_sold,
        COALESCE(SUM(oi.total_price), 0) as revenue,
        COALESCE(AVG(r.rating), 0) as avg_rating,
        COUNT(DISTINCT r.review_id) as review_count
    FROM products p
    LEFT JOIN order_items oi ON p.product_id = oi.product_id
    LEFT JOIN orders o ON oi.order_id = o.order_id 
        AND o.order_date BETWEEN p_date_from AND p_date_to
        AND o.order_status = 'completed'
    LEFT JOIN reviews r ON p.product_id = r.product_id
    WHERE p.category_id = p_category_id
        AND p.is_active = TRUE
    GROUP BY p.product_id, p.product_name, p.price
    ORDER BY revenue DESC, units_sold DESC
    LIMIT p_limit;
END //

DELIMITER ;

-- Call the procedure
CALL GetTopProductsByCategory(1, 10, '2024-01-01', '2024-12-31');
\`\`\`

## View and Procedure Management

### Managing Views and Procedures

\`\`\`sql
-- Show all views
SHOW FULL TABLES WHERE Table_type = 'VIEW';

-- Show view definition
SHOW CREATE VIEW customer_order_summary;

-- Drop and recreate view
DROP VIEW IF EXISTS customer_order_summary;

-- Show all stored procedures
SHOW PROCEDURE STATUS WHERE Db = 'your_database_name';

-- Show procedure definition
SHOW CREATE PROCEDURE ProcessOrder;

-- Drop procedure
DROP PROCEDURE IF EXISTS ProcessOrder;

-- Show all functions
SHOW FUNCTION STATUS WHERE Db = 'your_database_name';

-- Show function definition
SHOW CREATE FUNCTION CalculateShippingCost;

-- Drop function
DROP FUNCTION IF EXISTS CalculateShippingCost;
\`\`\`

### Performance Considerations

\`\`\`sql
-- Materialized view simulation (using tables)
CREATE TABLE materialized_customer_summary AS
SELECT * FROM customer_order_summary;

-- Add indexes to materialized view
CREATE INDEX idx_total_spent ON materialized_customer_summary(total_spent);
CREATE INDEX idx_last_order ON materialized_customer_summary(last_order_date);

-- Refresh materialized view
TRUNCATE TABLE materialized_customer_summary;
INSERT INTO materialized_customer_summary
SELECT * FROM customer_order_summary;

-- Create procedure to refresh materialized views
DELIMITER //

CREATE PROCEDURE RefreshMaterializedViews()
BEGIN
    DECLARE CONTINUE HANDLER FOR SQLEXCEPTION
    BEGIN
        ROLLBACK;
        RESIGNAL;
    END;
    
    START TRANSACTION;
    
    -- Refresh customer summary
    TRUNCATE TABLE materialized_customer_summary;
    INSERT INTO materialized_customer_summary
    SELECT * FROM customer_order_summary;
    
    -- Refresh product performance
    TRUNCATE TABLE materialized_product_performance;
    INSERT INTO materialized_product_performance
    SELECT * FROM product_performance;
    
    COMMIT;
    
    SELECT 'Materialized views refreshed successfully' as status;
END //

DELIMITER ;
\`\`\``,
    duration: '75 min',
    estimatedTime: 75,
    difficulty: 'advanced',
    order: 3
  },
  {
    id: 'sql-advanced-4',
    title: 'Window Functions and Advanced Analytics',
    description: 'Master window functions for advanced analytics including ranking, running totals, and statistical analysis.',
    content: `# Window Functions and Advanced Analytics

## Introduction to Window Functions

Window functions perform calculations across a set of table rows that are related to the current row, without collapsing rows like aggregate functions.

### Basic Window Function Syntax

\`\`\`sql
-- Basic window function structure
SELECT 
    column1,
    column2,
    window_function() OVER (
        [PARTITION BY partition_expression]
        [ORDER BY sort_expression]
        [frame_specification]
    ) as window_result
FROM table_name;
\`\`\`

## Ranking Functions

### ROW_NUMBER, RANK, and DENSE_RANK

\`\`\`sql
-- Compare different ranking functions
SELECT 
    customer_id,
    first_name,
    last_name,
    total_spent,
    -- ROW_NUMBER: Unique sequential numbers
    ROW_NUMBER() OVER (ORDER BY total_spent DESC) as row_num,
    -- RANK: Same values get same rank, gaps in sequence
    RANK() OVER (ORDER BY total_spent DESC) as rank_with_gaps,
    -- DENSE_RANK: Same values get same rank, no gaps
    DENSE_RANK() OVER (ORDER BY total_spent DESC) as dense_rank
FROM (
    SELECT 
        c.customer_id,
        c.first_name,
        c.last_name,
        COALESCE(SUM(o.total_amount), 0) as total_spent
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id AND o.order_status = 'completed'
    GROUP BY c.customer_id, c.first_name, c.last_name
) customer_totals
ORDER BY total_spent DESC;

-- Ranking within partitions (by category)
SELECT 
    p.product_id,
    p.product_name,
    c.category_name,
    p.price,
    sales_data.units_sold,
    -- Rank products within each category
    RANK() OVER (PARTITION BY c.category_name ORDER BY sales_data.units_sold DESC) as category_rank,
    -- Overall rank across all products
    RANK() OVER (ORDER BY sales_data.units_sold DESC) as overall_rank,
    -- Percentile rank within category
    PERCENT_RANK() OVER (PARTITION BY c.category_name ORDER BY sales_data.units_sold) as category_percentile
FROM products p
JOIN categories c ON p.category_id = c.category_id
LEFT JOIN (
    SELECT 
        oi.product_id,
        SUM(oi.quantity) as units_sold
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    WHERE o.order_status = 'completed'
    GROUP BY oi.product_id
) sales_data ON p.product_id = sales_data.product_id
WHERE p.is_active = TRUE
ORDER BY c.category_name, category_rank;
\`\`\`

### NTILE for Quartiles and Percentiles

\`\`\`sql
-- Divide customers into quartiles based on spending
SELECT 
    customer_id,
    first_name,
    last_name,
    total_spent,
    NTILE(4) OVER (ORDER BY total_spent) as spending_quartile,
    NTILE(10) OVER (ORDER BY total_spent) as spending_decile,
    CASE 
        WHEN NTILE(4) OVER (ORDER BY total_spent) = 4 THEN 'Top 25%'
        WHEN NTILE(4) OVER (ORDER BY total_spent) = 3 THEN 'Upper Middle 25%'
        WHEN NTILE(4) OVER (ORDER BY total_spent) = 2 THEN 'Lower Middle 25%'
        ELSE 'Bottom 25%'
    END as customer_segment
FROM (
    SELECT 
        c.customer_id,
        c.first_name,
        c.last_name,
        COALESCE(SUM(o.total_amount), 0) as total_spent
    FROM customers c
    LEFT JOIN orders o ON c.customer_id = o.customer_id AND o.order_status = 'completed'
    WHERE c.is_active = TRUE
    GROUP BY c.customer_id, c.first_name, c.last_name
) customer_spending
ORDER BY total_spent DESC;
\`\`\`

## Offset Functions

### LAG and LEAD

\`\`\`sql
-- Monthly sales analysis with previous/next month comparisons
SELECT 
    sale_year,
    sale_month,
    monthly_sales,
    -- Previous month sales
    LAG(monthly_sales, 1) OVER (ORDER BY sale_year, sale_month) as prev_month_sales,
    -- Next month sales
    LEAD(monthly_sales, 1) OVER (ORDER BY sale_year, sale_month) as next_month_sales,
    -- Month-over-month growth
    CASE 
        WHEN LAG(monthly_sales, 1) OVER (ORDER BY sale_year, sale_month) IS NOT NULL THEN
            ROUND(
                ((monthly_sales - LAG(monthly_sales, 1) OVER (ORDER BY sale_year, sale_month)) / 
                 LAG(monthly_sales, 1) OVER (ORDER BY sale_year, sale_month)) * 100, 
                2
            )
        ELSE NULL
    END as mom_growth_percent,
    -- Year-over-year comparison
    LAG(monthly_sales, 12) OVER (ORDER BY sale_year, sale_month) as same_month_prev_year,
    CASE 
        WHEN LAG(monthly_sales, 12) OVER (ORDER BY sale_year, sale_month) IS NOT NULL THEN
            ROUND(
                ((monthly_sales - LAG(monthly_sales, 12) OVER (ORDER BY sale_year, sale_month)) / 
                 LAG(monthly_sales, 12) OVER (ORDER BY sale_year, sale_month)) * 100, 
                2
            )
        ELSE NULL
    END as yoy_growth_percent
FROM (
    SELECT 
        YEAR(order_date) as sale_year,
        MONTH(order_date) as sale_month,
        SUM(total_amount) as monthly_sales
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY YEAR(order_date), MONTH(order_date)
) monthly_data
ORDER BY sale_year, sale_month;

-- Customer behavior analysis with lag/lead
SELECT 
    customer_id,
    order_date,
    total_amount,
    order_sequence,
    -- Days between orders
    DATEDIFF(
        order_date, 
        LAG(order_date, 1) OVER (PARTITION BY customer_id ORDER BY order_date)
    ) as days_since_last_order,
    -- Previous order amount
    LAG(total_amount, 1) OVER (PARTITION BY customer_id ORDER BY order_date) as prev_order_amount,
    -- Order amount trend
    CASE 
        WHEN LAG(total_amount, 1) OVER (PARTITION BY customer_id ORDER BY order_date) IS NOT NULL THEN
            CASE 
                WHEN total_amount > LAG(total_amount, 1) OVER (PARTITION BY customer_id ORDER BY order_date) THEN 'Increasing'
                WHEN total_amount < LAG(total_amount, 1) OVER (PARTITION BY customer_id ORDER BY order_date) THEN 'Decreasing'
                ELSE 'Stable'
            END
        ELSE 'First Order'
    END as spending_trend
FROM (
    SELECT 
        customer_id,
        order_date,
        total_amount,
        ROW_NUMBER() OVER (PARTITION BY customer_id ORDER BY order_date) as order_sequence
    FROM orders
    WHERE order_status = 'completed'
) customer_orders
ORDER BY customer_id, order_date;
\`\`\`

### FIRST_VALUE and LAST_VALUE

\`\`\`sql
-- Product pricing analysis with first and last values
SELECT 
    product_id,
    product_name,
    price_date,
    price,
    -- First price in the dataset
    FIRST_VALUE(price) OVER (
        PARTITION BY product_id 
        ORDER BY price_date 
        ROWS UNBOUNDED PRECEDING
    ) as original_price,
    -- Last price in the dataset
    LAST_VALUE(price) OVER (
        PARTITION BY product_id 
        ORDER BY price_date 
        ROWS BETWEEN UNBOUNDED PRECEDING AND UNBOUNDED FOLLOWING
    ) as current_price,
    -- Highest price ever
    MAX(price) OVER (
        PARTITION BY product_id 
        ORDER BY price_date 
        ROWS UNBOUNDED PRECEDING
    ) as highest_price_to_date,
    -- Lowest price ever
    MIN(price) OVER (
        PARTITION BY product_id 
        ORDER BY price_date 
        ROWS UNBOUNDED PRECEDING
    ) as lowest_price_to_date
FROM product_price_history
ORDER BY product_id, price_date;
\`\`\`

## Aggregate Window Functions

### Running Totals and Moving Averages

\`\`\`sql
-- Running totals and cumulative analysis
SELECT 
    order_date,
    daily_sales,
    -- Running total (cumulative sum)
    SUM(daily_sales) OVER (
        ORDER BY order_date 
        ROWS UNBOUNDED PRECEDING
    ) as running_total,
    -- 7-day moving average
    AVG(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 6 PRECEDING AND CURRENT ROW
    ) as seven_day_avg,
    -- 30-day moving average
    AVG(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 29 PRECEDING AND CURRENT ROW
    ) as thirty_day_avg,
    -- Running count of days
    COUNT(*) OVER (
        ORDER BY order_date 
        ROWS UNBOUNDED PRECEDING
    ) as days_count,
    -- Maximum daily sales up to current date
    MAX(daily_sales) OVER (
        ORDER BY order_date 
        ROWS UNBOUNDED PRECEDING
    ) as max_sales_to_date
FROM (
    SELECT 
        DATE(order_date) as order_date,
        SUM(total_amount) as daily_sales
    FROM orders
    WHERE order_status = 'completed'
        AND order_date >= DATE_SUB(NOW(), INTERVAL 1 YEAR)
    GROUP BY DATE(order_date)
) daily_sales_data
ORDER BY order_date;

-- Customer lifetime value calculation with running totals
SELECT 
    customer_id,
    order_date,
    order_amount,
    order_number,
    -- Customer's cumulative spending
    SUM(order_amount) OVER (
        PARTITION BY customer_id 
        ORDER BY order_date 
        ROWS UNBOUNDED PRECEDING
    ) as cumulative_spending,
    -- Customer's average order value up to this point
    AVG(order_amount) OVER (
        PARTITION BY customer_id 
        ORDER BY order_date 
        ROWS UNBOUNDED PRECEDING
    ) as avg_order_value_to_date,
    -- Days since customer's first order
    DATEDIFF(
        order_date,
        FIRST_VALUE(order_date) OVER (
            PARTITION BY customer_id 
            ORDER BY order_date 
            ROWS UNBOUNDED PRECEDING
        )
    ) as days_since_first_order
FROM (
    SELECT 
        customer_id,
        order_date,
        total_amount as order_amount,
        ROW_NUMBER() OVER (PARTITION BY customer_id ORDER BY order_date) as order_number
    FROM orders
    WHERE order_status = 'completed'
) customer_orders
ORDER BY customer_id, order_date;
\`\`\`

### Window Frames

\`\`\`sql
-- Different window frame specifications
SELECT 
    order_date,
    daily_sales,
    -- Default frame (RANGE UNBOUNDED PRECEDING)
    SUM(daily_sales) OVER (ORDER BY order_date) as cumulative_default,
    -- Explicit ROWS frame
    SUM(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW
    ) as cumulative_rows,
    -- RANGE frame (includes ties)
    SUM(daily_sales) OVER (
        ORDER BY order_date 
        RANGE BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW
    ) as cumulative_range,
    -- Moving window: last 7 days
    SUM(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 6 PRECEDING AND CURRENT ROW
    ) as last_7_days,
    -- Centered window: 3 days before to 3 days after
    AVG(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 3 PRECEDING AND 3 FOLLOWING
    ) as centered_7_day_avg,
    -- From current row to end
    SUM(daily_sales) OVER (
        ORDER BY order_date 
        ROWS BETWEEN CURRENT ROW AND UNBOUNDED FOLLOWING
    ) as remaining_total
FROM (
    SELECT 
        DATE(order_date) as order_date,
        SUM(total_amount) as daily_sales
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY DATE(order_date)
) daily_data
ORDER BY order_date;
\`\`\`

## Advanced Analytics with Window Functions

### Statistical Analysis

\`\`\`sql
-- Statistical analysis of customer spending patterns
WITH customer_stats AS (
    SELECT 
        customer_id,
        COUNT(*) as order_count,
        AVG(total_amount) as avg_order_value,
        STDDEV(total_amount) as stddev_order_value,
        MIN(total_amount) as min_order,
        MAX(total_amount) as max_order,
        SUM(total_amount) as total_spent
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY customer_id
)
SELECT 
    customer_id,
    order_count,
    avg_order_value,
    stddev_order_value,
    total_spent,
    -- Statistical rankings
    PERCENT_RANK() OVER (ORDER BY total_spent) as spending_percentile,
    PERCENT_RANK() OVER (ORDER BY avg_order_value) as aov_percentile,
    -- Z-score for total spending (how many standard deviations from mean)
    (total_spent - AVG(total_spent) OVER ()) / 
    NULLIF(STDDEV(total_spent) OVER (), 0) as spending_z_score,
    -- Coefficient of variation (relative variability)
    CASE 
        WHEN avg_order_value > 0 THEN stddev_order_value / avg_order_value
        ELSE NULL
    END as order_value_cv,
    -- Customer segment based on statistical analysis
    CASE 
        WHEN PERCENT_RANK() OVER (ORDER BY total_spent) >= 0.95 THEN 'Top 5%'
        WHEN PERCENT_RANK() OVER (ORDER BY total_spent) >= 0.80 THEN 'Top 20%'
        WHEN PERCENT_RANK() OVER (ORDER BY total_spent) >= 0.50 THEN 'Above Average'
        ELSE 'Below Average'
    END as statistical_segment
FROM customer_stats
WHERE order_count >= 2  -- Exclude single-order customers
ORDER BY total_spent DESC;
\`\`\`

### Cohort Analysis

\`\`\`sql
-- Customer cohort analysis by registration month
WITH customer_cohorts AS (
    SELECT 
        customer_id,
        DATE_FORMAT(created_at, '%Y-%m') as cohort_month,
        created_at as registration_date
    FROM customers
),
order_periods AS (
    SELECT 
        o.customer_id,
        c.cohort_month,
        TIMESTAMPDIFF(MONTH, c.registration_date, o.order_date) as period_number,
        o.total_amount
    FROM orders o
    JOIN customer_cohorts c ON o.customer_id = c.customer_id
    WHERE o.order_status = 'completed'
),
cohort_data AS (
    SELECT 
        cohort_month,
        period_number,
        COUNT(DISTINCT customer_id) as customers,
        SUM(total_amount) as revenue,
        AVG(total_amount) as avg_order_value
    FROM order_periods
    GROUP BY cohort_month, period_number
),
cohort_sizes AS (
    SELECT 
        cohort_month,
        COUNT(*) as cohort_size
    FROM customer_cohorts
    GROUP BY cohort_month
)
SELECT 
    cd.cohort_month,
    cs.cohort_size,
    cd.period_number,
    cd.customers,
    cd.revenue,
    cd.avg_order_value,
    -- Retention rate
    ROUND((cd.customers * 100.0 / cs.cohort_size), 2) as retention_rate,
    -- Revenue per customer in cohort
    ROUND(cd.revenue / cs.cohort_size, 2) as revenue_per_cohort_customer,
    -- Period-over-period retention change
    LAG(cd.customers) OVER (
        PARTITION BY cd.cohort_month 
        ORDER BY cd.period_number
    ) as prev_period_customers,
    CASE 
        WHEN LAG(cd.customers) OVER (PARTITION BY cd.cohort_month ORDER BY cd.period_number) IS NOT NULL THEN
            ROUND(
                ((cd.customers - LAG(cd.customers) OVER (PARTITION BY cd.cohort_month ORDER BY cd.period_number)) * 100.0 / 
                 LAG(cd.customers) OVER (PARTITION BY cd.cohort_month ORDER BY cd.period_number)), 
                2
            )
        ELSE NULL
    END as retention_change_percent
FROM cohort_data cd
JOIN cohort_sizes cs ON cd.cohort_month = cs.cohort_month
WHERE cs.cohort_size >= 50  -- Only include cohorts with meaningful size
ORDER BY cd.cohort_month, cd.period_number;
\`\`\`

### RFM Analysis (Recency, Frequency, Monetary)

\`\`\`sql
-- RFM Analysis for customer segmentation
WITH rfm_base AS (
    SELECT 
        customer_id,
        DATEDIFF(NOW(), MAX(order_date)) as recency_days,
        COUNT(*) as frequency,
        SUM(total_amount) as monetary_value
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY customer_id
),
rfm_scores AS (
    SELECT 
        customer_id,
        recency_days,
        frequency,
        monetary_value,
        -- Recency score (1-5, where 5 is most recent)
        CASE 
            WHEN recency_days <= 30 THEN 5
            WHEN recency_days <= 60 THEN 4
            WHEN recency_days <= 90 THEN 3
            WHEN recency_days <= 180 THEN 2
            ELSE 1
        END as recency_score,
        -- Frequency score (1-5, based on quintiles)
        NTILE(5) OVER (ORDER BY frequency) as frequency_score,
        -- Monetary score (1-5, based on quintiles)
        NTILE(5) OVER (ORDER BY monetary_value) as monetary_score
    FROM rfm_base
)
SELECT 
    customer_id,
    recency_days,
    frequency,
    monetary_value,
    recency_score,
    frequency_score,
    monetary_score,
    -- Combined RFM score
    CONCAT(recency_score, frequency_score, monetary_score) as rfm_code,
    -- RFM segment classification
    CASE 
        WHEN recency_score >= 4 AND frequency_score >= 4 AND monetary_score >= 4 THEN 'Champions'
        WHEN recency_score >= 3 AND frequency_score >= 3 AND monetary_score >= 3 THEN 'Loyal Customers'
        WHEN recency_score >= 4 AND frequency_score <= 2 THEN 'New Customers'
        WHEN recency_score >= 3 AND frequency_score <= 2 AND monetary_score >= 3 THEN 'Potential Loyalists'
        WHEN recency_score <= 2 AND frequency_score >= 3 AND monetary_score >= 3 THEN 'At Risk'
        WHEN recency_score <= 2 AND frequency_score >= 4 AND monetary_score >= 4 THEN 'Cannot Lose Them'
        WHEN recency_score <= 2 AND frequency_score <= 2 AND monetary_score >= 4 THEN 'Hibernating'
        WHEN recency_score <= 1 AND frequency_score <= 2 AND monetary_score <= 2 THEN 'Lost'
        ELSE 'Others'
    END as rfm_segment,
    -- Percentile ranks for benchmarking
    PERCENT_RANK() OVER (ORDER BY recency_days) as recency_percentile,
    PERCENT_RANK() OVER (ORDER BY frequency) as frequency_percentile,
    PERCENT_RANK() OVER (ORDER BY monetary_value) as monetary_percentile
FROM rfm_scores
ORDER BY recency_score DESC, frequency_score DESC, monetary_score DESC;
\`\`\``,
    duration: '70 min',
    estimatedTime: 70,
    difficulty: 'advanced',
    order: 4
  },
  {
    id: 'sql-advanced-5',
    title: 'Triggers, Events and Database Administration',
    description: 'Learn to create triggers for automated actions, schedule events, and perform essential database administration tasks.',
    content: `# Triggers, Events and Database Administration

## Database Triggers

### Basic Trigger Concepts

Triggers are special stored procedures that automatically execute (fire) in response to specific database events.

### BEFORE Triggers

\`\`\`sql
-- Trigger to validate and modify data before insertion
DELIMITER //

CREATE TRIGGER validate_customer_before_insert
BEFORE INSERT ON customers
FOR EACH ROW
BEGIN
    -- Validate email format
    IF NEW.email NOT REGEXP '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' THEN
        SIGNAL SQLSTATE '45000' SET MESSAGE_TEXT = 'Invalid email format';
    END IF;
    
    -- Standardize phone number format
    IF NEW.phone IS NOT NULL THEN
        SET NEW.phone = REGEXP_REPLACE(NEW.phone, '[^0-9]', '');
        IF LENGTH(NEW.phone) = 10 THEN
            SET NEW.phone = CONCAT('(', SUBSTRING(NEW.phone, 1, 3), ') ', 
                                 SUBSTRING(NEW.phone, 4, 3), '-', 
                                 SUBSTRING(NEW.phone, 7, 4));
        END IF;
    END IF;
    
    -- Ensure names are properly capitalized
    SET NEW.first_name = CONCAT(UPPER(LEFT(TRIM(NEW.first_name), 1)), 
                               LOWER(SUBSTRING(TRIM(NEW.first_name), 2)));
    SET NEW.last_name = CONCAT(UPPER(LEFT(TRIM(NEW.last_name), 1)), 
                              LOWER(SUBSTRING(TRIM(NEW.last_name), 2)));
    
    -- Set default values
    IF NEW.created_at IS NULL THEN
        SET NEW.created_at = NOW();
    END IF;
    
    IF NEW.is_active IS NULL THEN
        SET NEW.is_active = TRUE;
    END IF;
END //

-- Trigger to prevent negative inventory
CREATE TRIGGER check_inventory_before_update
BEFORE UPDATE ON products
FOR EACH ROW
BEGIN
    IF NEW.stock_quantity < 0 THEN
        SIGNAL SQLSTATE '45000' 
        SET MESSAGE_TEXT = 'Stock quantity cannot be negative';
    END IF;
    
    -- Update the last_modified timestamp
    SET NEW.updated_at = NOW();
    
    -- Log significant price changes
    IF ABS(NEW.price - OLD.price) / OLD.price > 0.1 THEN
        INSERT INTO price_change_log (product_id, old_price, new_price, change_percent, changed_at)
        VALUES (NEW.product_id, OLD.price, NEW.price, 
                ((NEW.price - OLD.price) / OLD.price) * 100, NOW());
    END IF;
END //

DELIMITER ;
\`\`\`

### AFTER Triggers

\`\`\`sql
DELIMITER //

-- Trigger to update customer statistics after order completion
CREATE TRIGGER update_customer_stats_after_order
AFTER INSERT ON orders
FOR EACH ROW
BEGIN
    -- Update customer totals
    INSERT INTO customer_statistics (
        customer_id, 
        total_orders, 
        total_spent, 
        avg_order_value, 
        last_order_date,
        updated_at
    )
    VALUES (
        NEW.customer_id,
        1,
        NEW.total_amount,
        NEW.total_amount,
        NEW.order_date,
        NOW()
    )
    ON DUPLICATE KEY UPDATE
        total_orders = total_orders + 1,
        total_spent = total_spent + NEW.total_amount,
        avg_order_value = (total_spent + NEW.total_amount) / (total_orders + 1),
        last_order_date = GREATEST(last_order_date, NEW.order_date),
        updated_at = NOW();
    
    -- Update customer tier based on new spending
    UPDATE customers 
    SET customer_tier = CASE 
        WHEN (SELECT total_spent FROM customer_statistics WHERE customer_id = NEW.customer_id) >= 10000 
        THEN 'Platinum'
        WHEN (SELECT total_spent FROM customer_statistics WHERE customer_id = NEW.customer_id) >= 5000 
        THEN 'Gold'
        WHEN (SELECT total_spent FROM customer_statistics WHERE customer_id = NEW.customer_id) >= 1000 
        THEN 'Silver'
        ELSE 'Bronze'
    END
    WHERE customer_id = NEW.customer_id;
END //

-- Trigger to maintain inventory after order items are added
CREATE TRIGGER update_inventory_after_order_item
AFTER INSERT ON order_items
FOR EACH ROW
BEGIN
    DECLARE v_order_status VARCHAR(50);
    
    -- Get order status
    SELECT order_status INTO v_order_status
    FROM orders 
    WHERE order_id = NEW.order_id;
    
    -- Only update inventory for confirmed orders
    IF v_order_status IN ('confirmed', 'processing', 'shipped') THEN
        UPDATE products 
        SET stock_quantity = stock_quantity - NEW.quantity,
            units_sold = COALESCE(units_sold, 0) + NEW.quantity,
            last_sold_date = NOW()
        WHERE product_id = NEW.product_id;
        
        -- Log inventory change
        INSERT INTO inventory_transactions (
            product_id, 
            transaction_type, 
            quantity, 
            reference_id, 
            transaction_date
        )
        VALUES (
            NEW.product_id, 
            'sale', 
            -NEW.quantity, 
            NEW.order_id, 
            NOW()
        );
    END IF;
END //

-- Audit trigger for sensitive data changes
CREATE TRIGGER audit_customer_changes
AFTER UPDATE ON customers
FOR EACH ROW
BEGIN
    INSERT INTO audit_log (
        table_name,
        record_id,
        action_type,
        old_values,
        new_values,
        changed_by,
        changed_at
    )
    VALUES (
        'customers',
        NEW.customer_id,
        'UPDATE',
        JSON_OBJECT(
            'first_name', OLD.first_name,
            'last_name', OLD.last_name,
            'email', OLD.email,
            'phone', OLD.phone,
            'customer_tier', OLD.customer_tier
        ),
        JSON_OBJECT(
            'first_name', NEW.first_name,
            'last_name', NEW.last_name,
            'email', NEW.email,
            'phone', NEW.phone,
            'customer_tier', NEW.customer_tier
        ),
        COALESCE(USER(), 'SYSTEM'),
        NOW()
    );
END //

DELIMITER ;
\`\`\`

### Complex Business Logic Triggers

\`\`\`sql
DELIMITER //

-- Complex order processing trigger
CREATE TRIGGER process_order_completion
AFTER UPDATE ON orders
FOR EACH ROW
BEGIN
    -- Only process when order status changes to 'completed'
    IF OLD.order_status != 'completed' AND NEW.order_status = 'completed' THEN
        
        -- Update product sales statistics
        UPDATE products p
        JOIN (
            SELECT 
                oi.product_id,
                SUM(oi.quantity) as sold_qty,
                SUM(oi.total_price) as revenue
            FROM order_items oi
            WHERE oi.order_id = NEW.order_id
            GROUP BY oi.product_id
        ) order_products ON p.product_id = order_products.product_id
        SET 
            p.total_revenue = COALESCE(p.total_revenue, 0) + order_products.revenue,
            p.units_sold_total = COALESCE(p.units_sold_total, 0) + order_products.sold_qty,
            p.last_sold_date = NEW.order_date;
        
        -- Calculate and award loyalty points
        SET @points_earned = FLOOR(NEW.total_amount * 0.1); -- 1 point per $10 spent
        
        -- Apply tier multiplier
        SET @tier_multiplier = CASE 
            WHEN (SELECT customer_tier FROM customers WHERE customer_id = NEW.customer_id) = 'Platinum' THEN 2.0
            WHEN (SELECT customer_tier FROM customers WHERE customer_id = NEW.customer_id) = 'Gold' THEN 1.5
            WHEN (SELECT customer_tier FROM customers WHERE customer_id = NEW.customer_id) = 'Silver' THEN 1.2
            ELSE 1.0
        END;
        
        SET @final_points = FLOOR(@points_earned * @tier_multiplier);
        
        -- Update customer loyalty points
        UPDATE customers 
        SET loyalty_points = COALESCE(loyalty_points, 0) + @final_points
        WHERE customer_id = NEW.customer_id;
        
        -- Log loyalty points transaction
        INSERT INTO loyalty_point_transactions (
            customer_id,
            transaction_type,
            points,
            description,
            reference_order_id,
            created_at
        )
        VALUES (
            NEW.customer_id,
            'earned',
            @final_points,
            CONCAT('Points earned from order #', NEW.order_id),
            NEW.order_id,
            NOW()
        );
        
        -- Check for milestone achievements
        CALL CheckCustomerMilestones(NEW.customer_id);
        
    END IF;
END //

DELIMITER ;
\`\`\`

## Scheduled Events

### Basic Event Scheduler

\`\`\`sql
-- Enable event scheduler
SET GLOBAL event_scheduler = ON;

-- Check event scheduler status
SHOW VARIABLES LIKE 'event_scheduler';
\`\`\`

### Creating Scheduled Events

\`\`\`sql
-- Daily maintenance event
DELIMITER //

CREATE EVENT daily_maintenance
ON SCHEDULE EVERY 1 DAY
STARTS '2024-01-01 02:00:00'
COMMENT 'Daily database maintenance tasks'
DO
BEGIN
    -- Update product statistics
    INSERT INTO daily_product_stats (
        stat_date,
        product_id,
        units_sold,
        revenue,
        avg_rating
    )
    SELECT 
        CURDATE() - INTERVAL 1 DAY as stat_date,
        oi.product_id,
        SUM(oi.quantity) as units_sold,
        SUM(oi.total_price) as revenue,
        COALESCE(AVG(r.rating), 0) as avg_rating
    FROM order_items oi
    JOIN orders o ON oi.order_id = o.order_id
    LEFT JOIN reviews r ON oi.product_id = r.product_id 
        AND DATE(r.created_at) = CURDATE() - INTERVAL 1 DAY
    WHERE DATE(o.order_date) = CURDATE() - INTERVAL 1 DAY
        AND o.order_status = 'completed'
    GROUP BY oi.product_id
    ON DUPLICATE KEY UPDATE
        units_sold = VALUES(units_sold),
        revenue = VALUES(revenue),
        avg_rating = VALUES(avg_rating);
    
    -- Clean up old sessions
    DELETE FROM user_sessions 
    WHERE last_activity < DATE_SUB(NOW(), INTERVAL 30 DAY);
    
    -- Archive old orders
    INSERT INTO orders_archive
    SELECT * FROM orders 
    WHERE order_date < DATE_SUB(NOW(), INTERVAL 2 YEAR)
        AND order_status IN ('completed', 'cancelled');
    
    -- Update customer segments
    CALL UpdateCustomerSegments();
    
    -- Log maintenance completion
    INSERT INTO maintenance_log (maintenance_type, completed_at, details)
    VALUES ('daily_maintenance', NOW(), 'Daily maintenance completed successfully');
    
END //

-- Weekly reporting event
CREATE EVENT weekly_reports
ON SCHEDULE EVERY 1 WEEK
STARTS '2024-01-07 06:00:00'  -- Sunday at 6 AM
COMMENT 'Weekly business reports generation'
DO
BEGIN
    DECLARE week_start DATE;
    DECLARE week_end DATE;
    
    SET week_start = DATE_SUB(CURDATE(), INTERVAL DAYOFWEEK(CURDATE()) + 6 DAY);
    SET week_end = DATE_SUB(CURDATE(), INTERVAL DAYOFWEEK(CURDATE()) DAY);
    
    -- Generate weekly sales report
    INSERT INTO weekly_sales_reports (
        week_starting,
        total_orders,
        total_revenue,
        avg_order_value,
        unique_customers,
        top_product_id,
        report_generated_at
    )
    SELECT 
        week_start,
        COUNT(*) as total_orders,
        SUM(total_amount) as total_revenue,
        AVG(total_amount) as avg_order_value,
        COUNT(DISTINCT customer_id) as unique_customers,
        (
            SELECT oi.product_id
            FROM order_items oi
            JOIN orders o2 ON oi.order_id = o2.order_id
            WHERE DATE(o2.order_date) BETWEEN week_start AND week_end
                AND o2.order_status = 'completed'
            GROUP BY oi.product_id
            ORDER BY SUM(oi.quantity) DESC
            LIMIT 1
        ) as top_product_id,
        NOW()
    FROM orders o
    WHERE DATE(o.order_date) BETWEEN week_start AND week_end
        AND o.order_status = 'completed';
    
    -- Generate customer retention report
    CALL GenerateWeeklyRetentionReport(week_start, week_end);
    
END //

-- Monthly cleanup event
CREATE EVENT monthly_cleanup
ON SCHEDULE EVERY 1 MONTH
STARTS '2024-01-01 01:00:00'
COMMENT 'Monthly database cleanup and optimization'
DO
BEGIN
    -- Clean up old temporary data
    DELETE FROM cart_items 
    WHERE created_at < DATE_SUB(NOW(), INTERVAL 30 DAY);
    
    DELETE FROM password_reset_tokens 
    WHERE created_at < DATE_SUB(NOW(), INTERVAL 24 HOUR);
    
    -- Clean up old audit logs (keep 1 year)
    DELETE FROM audit_log 
    WHERE changed_at < DATE_SUB(NOW(), INTERVAL 1 YEAR);
    
    -- Update materialized views
    CALL RefreshMaterializedViews();
    
    -- Analyze tables for optimization
    ANALYZE TABLE customers, orders, products, order_items;
    
    -- Log cleanup completion
    INSERT INTO maintenance_log (maintenance_type, completed_at, details)
    VALUES ('monthly_cleanup', NOW(), 'Monthly cleanup completed successfully');
    
END //

DELIMITER ;
\`\`\`

### Event Management

\`\`\`sql
-- Show all events
SHOW EVENTS;

-- Show specific event
SHOW CREATE EVENT daily_maintenance;

-- Disable an event
ALTER EVENT daily_maintenance DISABLE;

-- Enable an event
ALTER EVENT daily_maintenance ENABLE;

-- Modify event schedule
ALTER EVENT daily_maintenance
ON SCHEDULE EVERY 2 DAY
STARTS '2024-01-01 03:00:00';

-- Drop an event
DROP EVENT IF EXISTS old_event_name;

-- Event with one-time execution
CREATE EVENT one_time_data_migration
ON SCHEDULE AT '2024-12-31 23:59:59'
DO
BEGIN
    -- Migration code here
    CALL MigrateOldData();
    
    -- Log completion
    INSERT INTO migration_log (migration_name, completed_at)
    VALUES ('year_end_migration', NOW());
END;
\`\`\`

## Database Administration

### User Management and Security

\`\`\`sql
-- Create database users with specific privileges
CREATE USER 'app_read_only'@'%' IDENTIFIED BY 'secure_password_123';
CREATE USER 'app_read_write'@'%' IDENTIFIED BY 'secure_password_456';
CREATE USER 'reporting_user'@'%' IDENTIFIED BY 'secure_password_789';

-- Grant specific privileges
GRANT SELECT ON ecommerce.* TO 'app_read_only'@'%';

GRANT SELECT, INSERT, UPDATE, DELETE ON ecommerce.customers TO 'app_read_write'@'%';
GRANT SELECT, INSERT, UPDATE, DELETE ON ecommerce.orders TO 'app_read_write'@'%';
GRANT SELECT, INSERT, UPDATE, DELETE ON ecommerce.order_items TO 'app_read_write'@'%';

-- Grant reporting access
GRANT SELECT ON ecommerce.* TO 'reporting_user'@'%';
GRANT SELECT ON ecommerce.customer_statistics TO 'reporting_user'@'%';
GRANT SELECT ON ecommerce.weekly_sales_reports TO 'reporting_user'@'%';

-- Create role-based access
CREATE ROLE 'customer_service';
GRANT SELECT, UPDATE ON ecommerce.customers TO 'customer_service';
GRANT SELECT ON ecommerce.orders TO 'customer_service';
GRANT 'customer_service' TO 'service_agent1'@'%';

-- Show user privileges
SHOW GRANTS FOR 'app_read_write'@'%';

-- Revoke privileges
REVOKE DELETE ON ecommerce.customers FROM 'app_read_write'@'%';

-- Change user password
ALTER USER 'app_read_write'@'%' IDENTIFIED BY 'new_secure_password';
\`\`\`

### Backup and Recovery

\`\`\`sql
-- Create backup procedures
DELIMITER //

CREATE PROCEDURE CreateDatabaseBackup(
    IN backup_type VARCHAR(20),
    OUT backup_status VARCHAR(100)
)
BEGIN
    DECLARE backup_filename VARCHAR(255);
    DECLARE backup_command TEXT;
    
    SET backup_filename = CONCAT(
        'ecommerce_backup_',
        backup_type,
        '_',
        DATE_FORMAT(NOW(), '%Y%m%d_%H%i%s'),
        '.sql'
    );
    
    CASE backup_type
        WHEN 'full' THEN
            -- Full database backup
            SET backup_command = CONCAT(
                'mysqldump --single-transaction --routines --triggers ',
                '--databases ecommerce > /backups/', backup_filename
            );
        WHEN 'data_only' THEN
            -- Data only backup
            SET backup_command = CONCAT(
                'mysqldump --single-transaction --no-create-info ',
                'ecommerce > /backups/', backup_filename
            );
        WHEN 'schema_only' THEN
            -- Schema only backup
            SET backup_command = CONCAT(
                'mysqldump --no-data --routines --triggers ',
                'ecommerce > /backups/', backup_filename
            );
        ELSE
            SET backup_status = 'Invalid backup type';
    END CASE;
    
    -- Log backup attempt
    INSERT INTO backup_log (backup_type, filename, status, created_at)
    VALUES (backup_type, backup_filename, 'started', NOW());
    
    SET backup_status = CONCAT('Backup initiated: ', backup_filename);
    
END //

DELIMITER ;

-- Scheduled backup event
CREATE EVENT daily_backup
ON SCHEDULE EVERY 1 DAY
STARTS '2024-01-01 23:30:00'
COMMENT 'Daily database backup'
DO
BEGIN
    CALL CreateDatabaseBackup('full', @backup_status);
    
    -- Clean up old backups (keep 30 days)
    DELETE FROM backup_log 
    WHERE created_at < DATE_SUB(NOW(), INTERVAL 30 DAY);
    
END;
\`\`\`

### Performance Monitoring

\`\`\`sql
-- Performance monitoring procedures
DELIMITER //

CREATE PROCEDURE MonitorDatabasePerformance()
BEGIN
    -- Collect performance metrics
    INSERT INTO performance_metrics (
        metric_date,
        slow_queries_count,
        avg_query_time,
        connections_count,
        table_locks_waited,
        innodb_buffer_pool_usage,
        disk_usage_gb
    )
    SELECT 
        NOW(),
        (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Slow_queries'),
        (SELECT AVG(TIMER_END - TIMER_START) / 1000000000 FROM performance_schema.events_statements_history),
        (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Threads_connected'),
        (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Table_locks_waited'),
        (SELECT ROUND((1 - (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_pages_free') / 
                     (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_pages_total')) * 100, 2)),
        (SELECT ROUND(SUM(data_length + index_length) / 1024 / 1024 / 1024, 2) FROM information_schema.tables WHERE table_schema = 'ecommerce');
    
    -- Check for potential issues
    SET @slow_query_threshold = 100;
    SET @connection_threshold = 80;
    
    IF (SELECT slow_queries_count FROM performance_metrics ORDER BY metric_date DESC LIMIT 1) > @slow_query_threshold THEN
        INSERT INTO system_alerts (alert_type, message, severity, created_at)
        VALUES ('performance', 'High number of slow queries detected', 'warning', NOW());
    END IF;
    
    IF (SELECT connections_count FROM performance_metrics ORDER BY metric_date DESC LIMIT 1) > @connection_threshold THEN
        INSERT INTO system_alerts (alert_type, message, severity, created_at)
        VALUES ('performance', 'High number of database connections', 'warning', NOW());
    END IF;
    
END //

-- Index usage analysis
CREATE PROCEDURE AnalyzeIndexUsage()
BEGIN
    -- Find unused indexes
    SELECT 
        t.table_schema,
        t.table_name,
        t.index_name,
        'Unused index - consider dropping' as recommendation
    FROM information_schema.statistics t
    LEFT JOIN performance_schema.table_io_waits_summary_by_index_usage p 
        ON t.table_schema = p.object_schema 
        AND t.table_name = p.object_name 
        AND t.index_name = p.index_name
    WHERE t.table_schema = 'ecommerce'
        AND t.index_name != 'PRIMARY'
        AND p.index_name IS NULL;
    
    -- Find tables with high scan rates
    SELECT 
        object_schema,
        object_name,
        count_read as table_reads,
        count_read - count_fetch as full_table_scans,
        ROUND((count_read - count_fetch) / count_read * 100, 2) as scan_percentage,
        'Consider adding indexes' as recommendation
    FROM performance_schema.table_io_waits_summary_by_table
    WHERE object_schema = 'ecommerce'
        AND count_read > 1000
        AND (count_read - count_fetch) / count_read > 0.1
    ORDER BY scan_percentage DESC;
    
END //

DELIMITER ;

-- Schedule performance monitoring
CREATE EVENT monitor_performance
ON SCHEDULE EVERY 1 HOUR
COMMENT 'Hourly performance monitoring'
DO
BEGIN
    CALL MonitorDatabasePerformance();
END;
\`\`\`

### Database Maintenance

\`\`\`sql
-- Comprehensive maintenance procedures
DELIMITER //

CREATE PROCEDURE OptimizeDatabase()
BEGIN
    DECLARE done INT DEFAULT FALSE;
    DECLARE table_name VARCHAR(255);
    DECLARE table_cursor CURSOR FOR
        SELECT t.table_name
        FROM information_schema.tables t
        WHERE t.table_schema = 'ecommerce'
            AND t.table_type = 'BASE TABLE';
    
    DECLARE CONTINUE HANDLER FOR NOT FOUND SET done = TRUE;
    
    -- Log maintenance start
    INSERT INTO maintenance_log (maintenance_type, started_at)
    VALUES ('database_optimization', NOW());
    
    OPEN table_cursor;
    
    optimize_loop: LOOP
        FETCH table_cursor INTO table_name;
        
        IF done THEN
            LEAVE optimize_loop;
        END IF;
        
        -- Optimize each table
        SET @sql = CONCAT('OPTIMIZE TABLE ', table_name);
        PREPARE stmt FROM @sql;
        EXECUTE stmt;
        DEALLOCATE PREPARE stmt;
        
        -- Analyze table statistics
        SET @sql = CONCAT('ANALYZE TABLE ', table_name);
        PREPARE stmt FROM @sql;
        EXECUTE stmt;
        DEALLOCATE PREPARE stmt;
        
    END LOOP;
    
    CLOSE table_cursor;
    
    -- Update maintenance log
    UPDATE maintenance_log 
    SET completed_at = NOW(), 
        status = 'completed'
    WHERE maintenance_type = 'database_optimization' 
        AND completed_at IS NULL;
    
END //

DELIMITER ;

-- Schedule weekly optimization
CREATE EVENT weekly_optimization
ON SCHEDULE EVERY 1 WEEK
STARTS '2024-01-07 04:00:00'
COMMENT 'Weekly database optimization'
DO
BEGIN
    CALL OptimizeDatabase();
END;
\`\`\``,
    duration: '80 min',
    estimatedTime: 80,
    difficulty: 'advanced',
    order: 5
  },
  {
    id: 'sql-advanced-6',
    title: 'Advanced Indexing and Query Optimization',
    description: 'Master advanced indexing strategies, query optimization techniques, and performance tuning for large-scale databases.',
    content: `# Advanced Indexing and Query Optimization

## Index Types and Strategies

### B-Tree Indexes (Default)

\`\`\`sql
-- Basic index creation
CREATE INDEX idx_customer_email ON customers(email);
CREATE INDEX idx_order_date ON orders(order_date);
CREATE INDEX idx_product_price ON products(price);

-- Composite indexes (order matters!)
CREATE INDEX idx_order_customer_date ON orders(customer_id, order_date);
CREATE INDEX idx_product_category_price ON products(category_id, price);

-- Covering indexes (include all needed columns)
CREATE INDEX idx_customer_summary 
ON customers(customer_id, first_name, last_name, email, created_at);

-- Partial indexes with WHERE clause (MySQL 8.0+)
CREATE INDEX idx_active_customers 
ON customers(last_name, first_name) 
WHERE is_active = TRUE;

-- Functional indexes
CREATE INDEX idx_order_year ON orders((YEAR(order_date)));
CREATE INDEX idx_customer_domain ON customers((SUBSTRING(email, LOCATE('@', email) + 1)));
\`\`\`

### Specialized Index Types

\`\`\`sql
-- Full-text indexes for search
CREATE FULLTEXT INDEX idx_product_search 
ON products(product_name, description);

-- Use full-text search
SELECT product_id, product_name, description,
       MATCH(product_name, description) AGAINST('smartphone bluetooth' IN NATURAL LANGUAGE MODE) as relevance
FROM products
WHERE MATCH(product_name, description) AGAINST('smartphone bluetooth' IN NATURAL LANGUAGE MODE)
ORDER BY relevance DESC;

-- Boolean full-text search
SELECT product_id, product_name
FROM products
WHERE MATCH(product_name, description) 
AGAINST('+smartphone +camera -apple' IN BOOLEAN MODE);

-- Spatial indexes (for geographic data)
-- ALTER TABLE stores ADD COLUMN location POINT;
-- CREATE SPATIAL INDEX idx_store_location ON stores(location);

-- Hash indexes (Memory engine only)
-- CREATE TABLE session_data (
--     session_id VARCHAR(32) PRIMARY KEY,
--     user_id INT,
--     data TEXT,
--     INDEX USING HASH (user_id)
-- ) ENGINE=MEMORY;
\`\`\`

### Index Optimization Strategies

\`\`\`sql
-- Analyze index usage
SELECT 
    t.table_schema,
    t.table_name,
    t.index_name,
    t.column_name,
    t.seq_in_index,
    t.cardinality,
    CASE 
        WHEN p.index_name IS NULL THEN 'Unused'
        ELSE 'Used'
    END as usage_status,
    p.count_read,
    p.count_insert,
    p.count_update,
    p.count_delete
FROM information_schema.statistics t
LEFT JOIN performance_schema.table_io_waits_summary_by_index_usage p
    ON t.table_schema = p.object_schema 
    AND t.table_name = p.object_name 
    AND t.index_name = p.index_name
WHERE t.table_schema = 'ecommerce'
    AND t.index_name != 'PRIMARY'
ORDER BY t.table_name, t.index_name, t.seq_in_index;

-- Find duplicate or redundant indexes
SELECT 
    t1.table_name,
    t1.index_name as index1,
    t2.index_name as index2,
    GROUP_CONCAT(t1.column_name ORDER BY t1.seq_in_index) as columns1,
    GROUP_CONCAT(t2.column_name ORDER BY t2.seq_in_index) as columns2
FROM information_schema.statistics t1
JOIN information_schema.statistics t2 
    ON t1.table_schema = t2.table_schema 
    AND t1.table_name = t2.table_name
    AND t1.index_name < t2.index_name
WHERE t1.table_schema = 'ecommerce'
GROUP BY t1.table_name, t1.index_name, t2.index_name
HAVING columns1 = columns2
    OR columns1 = LEFT(columns2, LENGTH(columns1))
    OR columns2 = LEFT(columns1, LENGTH(columns2));

-- Index cardinality analysis
SELECT 
    table_name,
    index_name,
    column_name,
    cardinality,
    CASE 
        WHEN cardinality IS NULL THEN 'No statistics'
        WHEN cardinality < 10 THEN 'Very low cardinality'
        WHEN cardinality < 100 THEN 'Low cardinality'
        WHEN cardinality < 1000 THEN 'Medium cardinality'
        ELSE 'High cardinality'
    END as cardinality_level,
    CASE 
        WHEN cardinality < 10 THEN 'Consider removing index'
        WHEN cardinality < 100 THEN 'Review index necessity'
        ELSE 'Good for indexing'
    END as recommendation
FROM information_schema.statistics
WHERE table_schema = 'ecommerce'
    AND index_name != 'PRIMARY'
ORDER BY table_name, index_name, seq_in_index;
\`\`\`

## Query Optimization Techniques

### Query Execution Plan Analysis

\`\`\`sql
-- Analyze query execution plans
EXPLAIN FORMAT=JSON
SELECT 
    c.customer_id,
    c.first_name,
    c.last_name,
    COUNT(o.order_id) as order_count,
    SUM(o.total_amount) as total_spent
FROM customers c
LEFT JOIN orders o ON c.customer_id = o.customer_id 
    AND o.order_status = 'completed'
WHERE c.created_at >= '2024-01-01'
GROUP BY c.customer_id, c.first_name, c.last_name
HAVING total_spent > 1000
ORDER BY total_spent DESC
LIMIT 10;

-- Visual execution plan with costs
EXPLAIN FORMAT=TREE
SELECT 
    p.product_name,
    c.category_name,
    AVG(r.rating) as avg_rating,
    COUNT(r.review_id) as review_count
FROM products p
JOIN categories c ON p.category_id = c.category_id
LEFT JOIN reviews r ON p.product_id = r.product_id
WHERE p.is_active = TRUE
    AND p.price BETWEEN 50 AND 500
GROUP BY p.product_id, p.product_name, c.category_name
HAVING review_count >= 5
ORDER BY avg_rating DESC;

-- Analyze actual execution statistics
EXPLAIN ANALYZE
SELECT 
    o.order_id,
    o.order_date,
    c.first_name,
    c.last_name,
    SUM(oi.quantity * oi.unit_price) as order_total
FROM orders o
JOIN customers c ON o.customer_id = c.customer_id
JOIN order_items oi ON o.order_id = oi.order_id
WHERE o.order_date >= DATE_SUB(NOW(), INTERVAL 30 DAY)
    AND o.order_status = 'completed'
GROUP BY o.order_id, o.order_date, c.first_name, c.last_name
ORDER BY order_total DESC
LIMIT 20;
\`\`\`

### Query Rewriting and Optimization

\`\`\`sql
-- Original inefficient query
-- SELECT * FROM customers WHERE customer_id IN (
--     SELECT customer_id FROM orders WHERE order_date >= '2024-01-01'
-- );

-- Optimized version using EXISTS
SELECT c.*
FROM customers c
WHERE EXISTS (
    SELECT 1 FROM orders o 
    WHERE o.customer_id = c.customer_id 
    AND o.order_date >= '2024-01-01'
);

-- Original inefficient aggregate query
-- SELECT 
--     c.customer_id,
--     c.first_name,
--     (SELECT COUNT(*) FROM orders WHERE customer_id = c.customer_id) as order_count,
--     (SELECT SUM(total_amount) FROM orders WHERE customer_id = c.customer_id) as total_spent
-- FROM customers c;

-- Optimized version using JOIN
SELECT 
    c.customer_id,
    c.first_name,
    COALESCE(order_stats.order_count, 0) as order_count,
    COALESCE(order_stats.total_spent, 0) as total_spent
FROM customers c
LEFT JOIN (
    SELECT 
        customer_id,
        COUNT(*) as order_count,
        SUM(total_amount) as total_spent
    FROM orders
    WHERE order_status = 'completed'
    GROUP BY customer_id
) order_stats ON c.customer_id = order_stats.customer_id;

-- Using window functions instead of correlated subqueries
-- Original slow query
-- SELECT 
--     product_id,
--     product_name,
--     price,
--     (SELECT AVG(price) FROM products p2 WHERE p2.category_id = p1.category_id) as category_avg_price
-- FROM products p1;

-- Optimized with window function
SELECT 
    product_id,
    product_name,
    price,
    AVG(price) OVER (PARTITION BY category_id) as category_avg_price,
    price - AVG(price) OVER (PARTITION BY category_id) as price_vs_avg
FROM products
WHERE is_active = TRUE;
\`\`\`

### Advanced JOIN Optimization

\`\`\`sql
-- Efficient JOIN order and conditions
SELECT STRAIGHT_JOIN  -- Force MySQL to use this join order
    c.customer_id,
    c.first_name,
    c.last_name,
    o.order_date,
    p.product_name,
    oi.quantity
FROM customers c
FORCE INDEX (idx_customer_tier)  -- Force specific index usage
JOIN orders o ON c.customer_id = o.customer_id
JOIN order_items oi ON o.order_id = oi.order_id
JOIN products p ON oi.product_id = p.product_id
WHERE c.customer_tier = 'Platinum'
    AND o.order_date >= DATE_SUB(NOW(), INTERVAL 90 DAY)
    AND o.order_status = 'completed'
ORDER BY o.order_date DESC;

-- Optimize large table joins with derived tables
SELECT 
    recent_orders.customer_id,
    recent_orders.order_count,
    recent_orders.total_spent,
    c.first_name,
    c.last_name,
    c.customer_tier
FROM (
    -- Pre-aggregate to reduce join size
    SELECT 
        customer_id,
        COUNT(*) as order_count,
        SUM(total_amount) as total_spent
    FROM orders
    WHERE order_date >= DATE_SUB(NOW(), INTERVAL 30 DAY)
        AND order_status = 'completed'
    GROUP BY customer_id
    HAVING order_count >= 3
) recent_orders
JOIN customers c ON recent_orders.customer_id = c.customer_id
ORDER BY recent_orders.total_spent DESC;

-- Use hash joins for large datasets (MySQL 8.0.18+)
SELECT /*+ HASH_JOIN(c, o) */
    c.customer_id,
    c.first_name,
    COUNT(o.order_id) as order_count
FROM customers c
JOIN orders o ON c.customer_id = o.customer_id
WHERE c.created_at >= '2023-01-01'
GROUP BY c.customer_id, c.first_name;
\`\`\`

## Performance Tuning Strategies

### Query Cache and Buffer Pool Optimization

\`\`\`sql
-- Monitor buffer pool efficiency
SELECT 
    ROUND(
        (1 - (
            (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_reads') /
            (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Innodb_buffer_pool_read_requests')
        )) * 100, 
        2
    ) as buffer_pool_hit_rate;

-- Monitor query cache hit rate (if enabled)
SELECT 
    ROUND(
        (
            (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Qcache_hits') /
            (
                (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Qcache_hits') +
                (SELECT VARIABLE_VALUE FROM performance_schema.global_status WHERE VARIABLE_NAME = 'Com_select')
            )
        ) * 100,
        2
    ) as query_cache_hit_rate;

-- Identify tables causing buffer pool pressure
SELECT 
    object_schema,
    object_name,
    count_read,
    count_write,
    count_read + count_write as total_io,
    ROUND(sum_timer_read / count_read / 1000000000, 6) as avg_read_time_sec,
    ROUND(sum_timer_write / count_write / 1000000000, 6) as avg_write_time_sec
FROM performance_schema.table_io_waits_summary_by_table
WHERE object_schema = 'ecommerce'
    AND count_read + count_write > 1000
ORDER BY total_io DESC;
\`\`\`

### Partitioning for Large Tables

\`\`\`sql
-- Range partitioning by date
CREATE TABLE orders_partitioned (
    order_id INT AUTO_INCREMENT,
    customer_id INT NOT NULL,
    order_date DATE NOT NULL,
    order_status VARCHAR(50),
    total_amount DECIMAL(12,2),
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (order_id, order_date),
    INDEX idx_customer (customer_id),
    INDEX idx_status (order_status)
)
PARTITION BY RANGE (YEAR(order_date)) (
    PARTITION p2022 VALUES LESS THAN (2023),
    PARTITION p2023 VALUES LESS THAN (2024),
    PARTITION p2024 VALUES LESS THAN (2025),
    PARTITION p2025 VALUES LESS THAN (2026),
    PARTITION p_future VALUES LESS THAN MAXVALUE
);

-- Hash partitioning for even distribution
CREATE TABLE customer_activity_log (
    log_id BIGINT AUTO_INCREMENT,
    customer_id INT NOT NULL,
    activity_type VARCHAR(50),
    activity_data JSON,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (log_id, customer_id),
    INDEX idx_activity_type (activity_type),
    INDEX idx_created_at (created_at)
)
PARTITION BY HASH(customer_id)
PARTITIONS 8;

-- List partitioning by region
CREATE TABLE customers_by_region (
    customer_id INT AUTO_INCREMENT,
    first_name VARCHAR(50),
    last_name VARCHAR(50),
    region VARCHAR(20) NOT NULL,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    PRIMARY KEY (customer_id, region),
    INDEX idx_name (last_name, first_name)
)
PARTITION BY LIST COLUMNS(region) (
    PARTITION p_north VALUES IN ('North', 'Northeast', 'Northwest'),
    PARTITION p_south VALUES IN ('South', 'Southeast', 'Southwest'),
    PARTITION p_central VALUES IN ('Central', 'Midwest'),
    PARTITION p_west VALUES IN ('West', 'Pacific')
);

-- Partition maintenance
-- Add new partition
ALTER TABLE orders_partitioned 
ADD PARTITION (PARTITION p2026 VALUES LESS THAN (2027));

-- Drop old partition
ALTER TABLE orders_partitioned 
DROP PARTITION p2022;

-- Check partition usage
SELECT 
    table_name,
    partition_name,
    partition_ordinal_position,
    partition_method,
    partition_expression,
    table_rows,
    avg_row_length,
    data_length / 1024 / 1024 as data_mb
FROM information_schema.partitions
WHERE table_schema = 'ecommerce'
    AND partition_name IS NOT NULL
ORDER BY table_name, partition_ordinal_position;
\`\`\`

### Query Optimization Best Practices

\`\`\`sql
-- Optimize LIMIT with OFFSET for pagination
-- Inefficient for large offsets
-- SELECT * FROM products ORDER BY product_id LIMIT 10000, 20;

-- Efficient cursor-based pagination
SELECT * FROM products 
WHERE product_id > 10000 
ORDER BY product_id 
LIMIT 20;

-- Use covering indexes to avoid table lookups
CREATE INDEX idx_order_covering 
ON orders(customer_id, order_date, order_status, total_amount);

-- Query uses only indexed columns
SELECT customer_id, order_date, order_status, total_amount
FROM orders
WHERE customer_id = 123
    AND order_date >= '2024-01-01'
ORDER BY order_date DESC;

-- Optimize COUNT queries
-- Slow for large tables
-- SELECT COUNT(*) FROM orders WHERE order_status = 'completed';

-- Faster with approximate count
SELECT table_rows 
FROM information_schema.tables 
WHERE table_schema = 'ecommerce' 
    AND table_name = 'orders';

-- Or use index for filtered counts
CREATE INDEX idx_orders_status ON orders(order_status);
-- Now COUNT with WHERE on indexed column is faster

-- Batch processing for large updates
DELIMITER //

CREATE PROCEDURE BatchUpdateCustomerTiers()
BEGIN
    DECLARE done INT DEFAULT FALSE;
    DECLARE batch_size INT DEFAULT 1000;
    DECLARE processed INT DEFAULT 0;
    
    -- Process in batches to avoid lock contention
    batch_loop: LOOP
        UPDATE customers 
        SET customer_tier = CASE 
            WHEN total_spent >= 10000 THEN 'Platinum'
            WHEN total_spent >= 5000 THEN 'Gold'
            WHEN total_spent >= 1000 THEN 'Silver'
            ELSE 'Bronze'
        END
        WHERE customer_id BETWEEN processed + 1 AND processed + batch_size;
        
        SET processed = processed + batch_size;
        
        -- Check if we've processed all customers
        IF ROW_COUNT() < batch_size THEN
            LEAVE batch_loop;
        END IF;
        
        -- Small delay to reduce system load
        SELECT SLEEP(0.1);
        
    END LOOP;
    
END //

DELIMITER ;
\`\`\`

### Performance Monitoring and Alerting

\`\`\`sql
-- Create performance monitoring table
CREATE TABLE query_performance_log (
    log_id BIGINT AUTO_INCREMENT PRIMARY KEY,
    query_hash VARCHAR(64),
    query_text TEXT,
    execution_time_sec DECIMAL(10,6),
    rows_examined BIGINT,
    rows_sent BIGINT,
    created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP,
    INDEX idx_execution_time (execution_time_sec),
    INDEX idx_created_at (created_at)
);

-- Monitor slow queries
DELIMITER //

CREATE PROCEDURE MonitorSlowQueries()
BEGIN
    -- Log queries taking longer than 1 second
    INSERT INTO query_performance_log (
        query_hash,
        query_text,
        execution_time_sec,
        rows_examined,
        rows_sent
    )
    SELECT 
        SUBSTRING(MD5(sql_text), 1, 16) as query_hash,
        LEFT(sql_text, 1000) as query_text,
        timer_end / 1000000000000 as execution_time_sec,
        rows_examined,
        rows_sent
    FROM performance_schema.events_statements_history_long
    WHERE timer_end / 1000000000000 > 1.0
        AND sql_text NOT LIKE 'INSERT INTO query_performance_log%';
    
    -- Alert on excessive slow queries
    SET @slow_query_count = (
        SELECT COUNT(*) 
        FROM query_performance_log 
        WHERE created_at >= DATE_SUB(NOW(), INTERVAL 1 HOUR)
            AND execution_time_sec > 5.0
    );
    
    IF @slow_query_count > 10 THEN
        INSERT INTO system_alerts (alert_type, message, severity, created_at)
        VALUES (
            'performance', 
            CONCAT('Detected ', @slow_query_count, ' queries taking over 5 seconds in the last hour'),
            'high',
            NOW()
        );
    END IF;
    
END //

DELIMITER ;

-- Schedule performance monitoring
CREATE EVENT monitor_query_performance
ON SCHEDULE EVERY 15 MINUTE
COMMENT 'Monitor query performance every 15 minutes'
DO
BEGIN
    CALL MonitorSlowQueries();
END;

-- Generate optimization recommendations
DELIMITER //

CREATE PROCEDURE GenerateOptimizationRecommendations()
BEGIN
    -- Analyze table scans
    SELECT 
        'HIGH_TABLE_SCAN' as issue_type,
        CONCAT('Table ', object_name, ' has high scan ratio: ', 
               ROUND((count_read - count_fetch) / count_read * 100, 2), '%') as recommendation,
        'Consider adding indexes' as action
    FROM performance_schema.table_io_waits_summary_by_table
    WHERE object_schema = 'ecommerce'
        AND count_read > 1000
        AND (count_read - count_fetch) / count_read > 0.3
    
    UNION ALL
    
    -- Analyze unused indexes
    SELECT 
        'UNUSED_INDEX' as issue_type,
        CONCAT('Index ', index_name, ' on table ', table_name, ' appears unused') as recommendation,
        'Consider dropping index' as action
    FROM information_schema.statistics s
    LEFT JOIN performance_schema.table_io_waits_summary_by_index_usage p
        ON s.table_schema = p.object_schema 
        AND s.table_name = p.object_name 
        AND s.index_name = p.index_name
    WHERE s.table_schema = 'ecommerce'
        AND s.index_name != 'PRIMARY'
        AND p.count_read IS NULL
    
    UNION ALL
    
    -- Analyze slow queries
    SELECT 
        'SLOW_QUERY' as issue_type,
        CONCAT('Query pattern appears frequently in slow log: ', LEFT(query_text, 100)) as recommendation,
        'Optimize query or add indexes' as action
    FROM (
        SELECT 
            query_text,
            COUNT(*) as occurrence_count,
            AVG(execution_time_sec) as avg_execution_time
        FROM query_performance_log
        WHERE created_at >= DATE_SUB(NOW(), INTERVAL 24 HOUR)
        GROUP BY LEFT(query_text, 100)
        HAVING occurrence_count > 5 AND avg_execution_time > 2.0
    ) slow_patterns;
    
END //

DELIMITER ;
\`\`\``,
    duration: '85 min',
    estimatedTime: 85,
    difficulty: 'advanced',
    order: 6
  }
];
